/**
 * The jog dial.
 *
 * A physical control, drawn: a knurled ring, a machined face, an indicator dot and a lamp
 * that follows it. Skeuomorphic on purpose — this is the one screen in the app pretending
 * to be an object, and a remote that looks like a remote is easier to use without looking
 * at it than a row of flat rectangles.
 *
 * ## Why it seeks rather than sets volume
 *
 * A rotary dial's most obvious use is volume, and that is exactly what it must not do
 * here: the television cannot change its volume in this build — Tizen exposes that through
 * `tizen.tvaudiocontrol`, which the widget has no privilege for. A dial that turned
 * smoothly and did nothing would be worse than no dial.
 *
 * Seeking, it can do. So this is a jog wheel: rotation accumulates into seconds, the
 * readout shows where you are about to land, and the seek is committed on release. One
 * full turn is `SECONDS_PER_TURN`, which makes a two-hour film about eight turns — coarse
 * enough to cross an act, fine enough to find the start of a scene.
 *
 * ## Accessible without a second control
 *
 * `role="slider"` with the usual aria attributes and arrow-key handling, so it is a
 * seekable position to a screen reader and to a keyboard rather than a decorative circle.
 * The alternative — a hidden range input alongside — would be two sources of truth for the
 * same value.
 */
import { useCallback, useRef, useState } from 'react';

/** One full rotation. Chosen so a feature film is a handful of turns, not forty. */
const SECONDS_PER_TURN = 900;
/** Arrow keys nudge; Page keys jump. Same steps the transport buttons use. */
const KEY_STEP = 10;
const KEY_PAGE = 60;

function angleOf(element: HTMLElement, clientX: number, clientY: number): number {
  const box = element.getBoundingClientRect();
  const cx = box.left + box.width / 2;
  const cy = box.top + box.height / 2;
  return (Math.atan2(clientY - cy, clientX - cx) * 180) / Math.PI;
}

export function JogDial({
  position,
  duration,
  disabled,
  onPreview,
  onCommit,
}: {
  position: number;
  duration: number;
  disabled?: boolean;
  /** Fires continuously while turning, so the readout can follow the hand. */
  onPreview: (seconds: number) => void;
  /** Fires once, on release. */
  onCommit: (seconds: number) => void;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const lastAngle = useRef<number | null>(null);
  const pending = useRef(position);
  const [turning, setTurning] = useState(false);
  /** Visual rotation only — it keeps winding past 360° so the dot does not snap back. */
  const [spin, setSpin] = useState(0);

  // `useCallback` so the handlers below can depend on it by name. It was a plain arrow
  // recreated per render, which worked only because those handlers happened to list
  // `duration` — the thing it closes over — and nothing said they had to keep doing so.
  const clamp = useCallback(
    (value: number) => Math.max(0, Math.min(duration || 0, value)),
    [duration],
  );

  const begin = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (disabled) return;
      const element = ref.current;
      if (!element) return;

      /*
       * The gesture is armed first, and capture is best-effort second.
       *
       * `setPointerCapture` throws `NotFoundError` when the id names no active pointer —
       * a pointer released in the gap before the handler runs, some pen and touch edge
       * cases, and any synthetic event. It used to be the first statement here, so when it
       * threw, `lastAngle` was never set and the dial went dead for the whole drag with
       * nothing on screen to say why. Capture is a refinement; losing it costs a gesture
       * that ends at the edge of the dial, which is survivable. Losing the gesture is not.
       */
      lastAngle.current = angleOf(element, e.clientX, e.clientY);
      pending.current = position;
      setTurning(true);

      try {
        // So a finger that slides off the dial mid-turn keeps controlling it.
        element.setPointerCapture(e.pointerId);
      } catch {
        /* no capture; the pointer handlers on the element still fire while it is over it */
      }
    },
    [disabled, position],
  );

  const move = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const element = ref.current;
      if (!element || lastAngle.current === null) return;

      const now = angleOf(element, e.clientX, e.clientY);
      let delta = now - lastAngle.current;
      /*
       * `atan2` jumps from +180 to -180 as the hand crosses the top of the dial. Without
       * this the accumulated value lurches by a whole turn at exactly the twelve o'clock
       * position — which is where a thumb naturally starts.
       */
      if (delta > 180) delta -= 360;
      if (delta < -180) delta += 360;
      lastAngle.current = now;

      pending.current = clamp(pending.current + (delta / 360) * SECONDS_PER_TURN);
      setSpin((s) => s + delta);
      onPreview(pending.current);
    },
    [onPreview, clamp],
  );

  const end = useCallback(() => {
    if (lastAngle.current === null) return;
    lastAngle.current = null;
    setTurning(false);
    onCommit(pending.current);
  }, [onCommit]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    const step =
      e.key === 'ArrowRight' || e.key === 'ArrowUp' ? KEY_STEP
      : e.key === 'ArrowLeft' || e.key === 'ArrowDown' ? -KEY_STEP
      : e.key === 'PageUp' ? KEY_PAGE
      : e.key === 'PageDown' ? -KEY_PAGE
      : null;
    if (step === null) return;

    e.preventDefault();
    const target = clamp(position + step);
    onPreview(target);
    onCommit(target);
  };

  return (
    <div
      ref={ref}
      className={`jog${turning ? ' is-turning' : ''}${disabled ? ' is-disabled' : ''}`}
      role="slider"
      tabIndex={disabled ? -1 : 0}
      aria-label="Konum"
      aria-valuemin={0}
      aria-valuemax={Math.round(duration) || 0}
      aria-valuenow={Math.round(position)}
      aria-disabled={disabled || undefined}
      onPointerDown={begin}
      onPointerMove={move}
      onPointerUp={end}
      onPointerCancel={end}
      onKeyDown={onKeyDown}
    >
      {/* The two hairlines of the reference piece, which is what stops the outer ring
          reading as a flat circle. */}
      <span className="jog__line" aria-hidden />
      <span className="jog__line" aria-hidden />

      {/* The lamp trails the indicator. Transformed rather than repositioned, so it stays
          on the compositor while the dial is being turned. */}
      <span className="jog__light" style={{ transform: `rotate(${spin - 90}deg)` }} aria-hidden>
        <i />
      </span>
      <span className="jog__dot" style={{ transform: `rotate(${spin - 90}deg)` }} aria-hidden>
        <i />
      </span>

      <span className="jog__face" aria-hidden>
        <span className="jog__face2">
          <span className="jog__cap" />
        </span>
      </span>
    </div>
  );
}
